import { Error } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { AirTimeRegistrationInjection } from "features/airTimeRegistration/di/AirTimeRegistrationInjection";
import { AirTimeRegistrationDetailEntity } from "features/airTimeRegistration/domain/entities/AirTimeRegistrationDetailEntity";
import {
  ExportPayload,
  FetchListInput,
} from "features/airTimeRegistration/domain/entities/AirTimeRegistrationPayload";
import { AirTimeRegistrationRequestEntity } from "features/airTimeRegistration/domain/entities/AirTimeRegistrationRequestEntity";
import { AirTimeRegistrationResponseEntity } from "features/airTimeRegistration/domain/entities/AirTimeRegistrationResponseEntity";
import { DenominationListRequestEntity } from "features/airTimeRegistration/domain/entities/DenominationListRequestEntity";
import { StoreListRequestEntiy } from "features/airTimeRegistration/domain/entities/StoreListRequestEntity";
import { isEmpty } from "lodash";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { ServiceEnum } from "utils/enums/ServiceEnum";
import { DateFormat } from "utils/format/DateFormat";
import { StringFormat } from "utils/format/StringFormat";
import { strings } from "utils/localizedStrings";
import { IAirTimeRegistrationObservedState } from "../interfaces/IAirTimeRegistration";

type RoleFilterState = Pick<
  IAirTimeRegistrationObservedState,
  "storeOptions" | "staffOptions" | "userOptions" | "isStoreFilterDisabled"
>;

class AirTimeRegistrationInput implements Disposable {
  public fetchList = new BehaviorSubject<FetchListInput>({
    kind: "params",
    params: new AirTimeRegistrationRequestEntity(),
  });
  public fetchCarrier = new BehaviorSubject<BaseFilterListRequestModel>({});
  public exportExcel = new BehaviorSubject<ExportPayload | null>(null);
  public fetchDetail = new BehaviorSubject<AirTimeRegistrationRequestEntity>(
    new AirTimeRegistrationRequestEntity(),
  );
  public fetchPartners = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loadStores = new BehaviorSubject<StoreListRequestEntiy>(
    new StoreListRequestEntiy(),
  );
  public loadStaff = new BehaviorSubject<BaseUserRequestModel>({});
  public loadUsers = new BehaviorSubject<BaseUserRequestModel>({});
  public fetchDenomination = new BehaviorSubject<DenominationListRequestEntity>(
    new DenominationListRequestEntity(),
  );
  public syncRoleFilters = new BehaviorSubject<Partial<RoleFilterState>>({});

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchCarrier.complete();
    this.exportExcel.complete();
    this.fetchDetail.complete();
    this.fetchPartners.complete();
    this.loadStores.complete();
    this.loadStaff.complete();
    this.loadUsers.complete();
    this.fetchDenomination.complete();
    this.syncRoleFilters.complete();
  }
}

class AirTimeRegistrationOutput implements Disposable {
  public onError = new BehaviorSubject<Error>({});
  public onExportSuccess = new BehaviorSubject<Blob | null>(null);
  public onGetDetailSuccess = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.onError.complete();
    this.onExportSuccess.complete();
    this.onGetDetailSuccess.complete();
  }
}

@injectable()
export class AirTimeRegistrationViewModel extends BasedObservedViewModel<
  AirTimeRegistrationInput,
  AirTimeRegistrationOutput,
  IAirTimeRegistrationObservedState
> {
  private airTimeRegistrationUseCase =
    AirTimeRegistrationInjection.getAirTimeRegistrationUseCase();

  constructor() {
    super(new AirTimeRegistrationInput(), new AirTimeRegistrationOutput());
  }

  protected override createObservedState(): IAirTimeRegistrationObservedState {
    return {
      list: [],
      detail: null,
      pagination: { page: 0, total: 0, limit: 10 },
      loading: false,
      detailLoading: false,
      queryParams: new AirTimeRegistrationRequestEntity({
        page: 0,
        limit: 10,
        ...DateFormat.getDateRange(0),
        service: ServiceEnum.AIR_TIME,
      }),
      carrier: [],
      partnerOptions: [],
      storeOptions: [],
      staffOptions: [],
      userOptions: [{ label: strings.GlobalLabels.All, value: "" }],
      denominationList: [],
      isStoreFilterDisabled: false,
      summary: {},
    };
  }

  public shouldUsePrivateTransactionList(): boolean {
    return this.airTimeRegistrationUseCase.shouldUsePrivateTransactionList();
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (input, guard) => {
      try {
        guard?.runIfLatest(() => {
          const pagination =
            input.kind === "tableChange"
              ? {
                  ...this.observedState.pagination,
                  page: input.page,
                  limit: input.limit,
                }
              : { ...this.observedState.pagination, page: 0, total: 0 };
          this.setObservedState({
            loading: true,
            list: input.kind === "params" ? this.observedState.list : [],
            summary: input.kind === "params" ? this.observedState.summary : {},
            pagination,
          });
        });

        let response;
        let queryParams: AirTimeRegistrationRequestEntity | null = null;
        switch (input.kind) {
          case "search": {
            const result =
              await this.airTimeRegistrationUseCase.getAirTimeRegistrationListFromSearch(
                input.rawFormValues,
                input.context,
              );
            response = result.list;
            queryParams = result.params;
            break;
          }
          case "tableChange": {
            const result =
              await this.airTimeRegistrationUseCase.getAirTimeRegistrationListWithTableChange(
                input.prevParams,
                input.rawFormValues,
                input.page,
                input.limit,
              );
            response = result.list;
            queryParams = result.params;
            break;
          }
          case "redo": {
            const result =
              await this.airTimeRegistrationUseCase.getAirTimeRegistrationListRedo();
            response = result.list;
            queryParams = result.params;
            break;
          }
          case "params":
          default:
            response =
              await this.airTimeRegistrationUseCase.getAirTimeRegistrationList(
                input.params,
              );
            queryParams = input.params;
            break;
        }

        guard?.runIfLatest(() => {
          this.setObservedState({
            list: (response?.data ?? []).map(
              (item) => new AirTimeRegistrationResponseEntity(item),
            ),
            pagination: response?.pagination ?? {},
            summary: response?.total ?? {
              success: 0,
              error: 0,
              other: 0,
              amount: 0,
              commission: 0,
            },
            ...(queryParams ? { queryParams } : {}),
          });
        });
      } catch (error) {
        console.error(error);
        guard?.runIfLatest(() => this.setObservedState({ list: [] }));
        this.output.onError.next(error as Error);
      } finally {
        guard?.runIfLatest(() => this.setObservedState({ loading: false }));
      }
    });

    this.skipFirstInitial(this.input.fetchCarrier, async (params) => {
      try {
        const response = await this.airTimeRegistrationUseCase.getFilterList(
          params,
          "carrier",
        );
        this.setObservedState({
          carrier: [
            { label: strings.GlobalLabels.All, value: "" },
            ...(response ?? []).map((item) => ({
              label: item.name,
              value: item.code,
            })),
          ],
        });
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      if (!params?.code) return;
      try {
        this.setObservedState({ detailLoading: true, detail: null });
        const response =
          await this.airTimeRegistrationUseCase.getAirTimeRegistrationDetail(
            params,
          );
        if (response?.data) {
          this.setObservedState({
            detail: new AirTimeRegistrationDetailEntity(response.data),
          });
          this.output.onGetDetailSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      } finally {
        this.setObservedState({ detailLoading: false });
      }
    });

    this.skipFirstInitial(this.input.exportExcel, async (payload, guard) => {
      if (!payload) return;
      try {
        guard?.runIfLatest(() => this.setObservedState({ loading: true }));
        const response =
          await this.airTimeRegistrationUseCase.exportAirTimeRegistrationReportFromSearch(
            payload,
          );
        if (response?.data?.size < 1000) {
          this.output.onError.next({ message: strings.GlobalMessage.ExcelError });
          return;
        }
        if (response?.data) this.output.onExportSuccess.next(response.data);
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      } finally {
        guard?.runIfLatest(() => this.setObservedState({ loading: false }));
      }
    });

    this.skipFirstInitial(this.input.fetchPartners, async (params) => {
      try {
        const cachedPartners = productCache.getCache(CacheKeyEnum.partnerList);
        const partners = !isEmpty(cachedPartners)
          ? cachedPartners
          : await this.airTimeRegistrationUseCase.getFilterList(
              params,
              "partner",
            );
        if (isEmpty(cachedPartners)) {
          productCache.saveCache({
            key: CacheKeyEnum.partnerList,
            product: partners ?? [],
          });
        }
        this.setObservedState({
          partnerOptions: (partners ?? []).map((partner) => ({
            label: partner.name,
            value: partner.code,
            type: partner.type,
            isStore:
              StringFormat.parseNoteString(partner.note)?.["isChain"] ===
              "true",
            providerCode: partner.providerCode,
          })),
        });
      } catch (error) {
        console.error("Error fetching partners:", error);
        this.setObservedState({ partnerOptions: [] });
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.loadStores, async (params) => {
      try {
        if (!params.partnerCode) {
          this.setObservedState({ storeOptions: [] });
          return;
        }
        const response = await this.airTimeRegistrationUseCase.getFilterList(
          params,
          "store",
        );
        const options = (response ?? []).map((store) => ({
          label: store.name,
          value: store.code,
        }));
        this.setObservedState({
          isStoreFilterDisabled: isEmpty(response),
          storeOptions:
            options.length > 0
              ? [{ label: strings.GlobalLabels.All, value: "" }, ...options]
              : [{ label: strings.GlobalMessage.InvalidStores, value: "" }],
        });
      } catch (error) {
        this.setObservedState({ storeOptions: [] });
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.loadStaff, async (params) => {
      if (isEmpty(params?.storeCode)) {
        this.setObservedState({ staffOptions: [] });
        return;
      }
      try {
        const response = await this.airTimeRegistrationUseCase.getUserForFilter(
          params,
          "staff",
        );
        const users = (response ?? []).flatMap((item: any) => item?.users ?? []);
        this.setObservedState({
          staffOptions: [
            { label: strings.GlobalLabels.All, value: "" },
            ...users.map((user: any) => ({
              label:
                user.name || user.userName || user.email || user.phone || "",
              value: user.id,
            })),
          ],
        });
      } catch (error) {
        this.setObservedState({ staffOptions: [] });
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.loadUsers, async (params) => {
      try {
        const target =
          params.providerCode === "mobitopup" ? "staff" : "userB2B";
        const response = await this.airTimeRegistrationUseCase.getUserForFilter(
          params,
          target,
        );
        const users = (response ?? []).flatMap((item: any) => item?.users ?? []);
        const mappedUsers: BaseUserResponseModel[] = users.map((user: any) => ({
          id: user.id,
          name: user.fullName || user.email || user.name || user.phone || "",
          code: user.code,
        }));
        this.setObservedState({
          userOptions: [
            { label: strings.GlobalLabels.All, value: "" },
            ...mappedUsers.map((user) => ({
              label: user.name,
              value: user.id,
            })),
          ],
        });
      } catch (error) {
        this.setObservedState({ userOptions: [] });
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.fetchDenomination, async (params) => {
      try {
        const query: DenominationListRequestEntity = { ...params };
        if (params.providerCode) query.providerCode = params.providerCode;
        if (Array.isArray(params.carrier)) {
          params.carrier
            .filter(Boolean)
            .forEach((code, index) => {
              query[`carrier[${index}]`] = code;
            });
        }
        const response = await this.airTimeRegistrationUseCase.getFilterList(
          query,
          "denomination",
        );
        this.setObservedState({
          denominationList: [
            { label: strings.GlobalLabels.All, value: "" },
            ...(response ?? []).map((item) => ({
              label: item.name,
              value: item.code,
            })),
          ],
        });
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.syncRoleFilters, (state) => {
      this.setObservedState(state);
    });
  }
}
